/**
 * ProgressBar — shared aspect. Horizontal segmented progress bar.
 * Past segments use completedColor (default LIVE_GREEN), the active
 * segment uses theme.accent, future ones use theme.border.
 *
 * Used by Aspect 22 ProgramCard's "Phase n/N" bar; reusable for any
 * stepwise / phase-progress display.
 */
import { View } from "react-native";

import { COLORS } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type ProgressBarProps = {
  theme: PaneTheme;
  /** Total segment count. */
  segments: number;
  /** 1-based index of the active (current) segment. */
  current: number;
  /** Per-segment height. Default 8. */
  height?: number;
  /** Override the completed-segment color (default COLORS.LIVE_GREEN.hex). */
  completedColor?: string;
};

export function ProgressBar({
  theme,
  segments,
  current,
  height = 8,
  completedColor = COLORS.LIVE_GREEN.hex,
}: ProgressBarProps) {
  return (
    <View style={{ flexDirection: "row", gap: 4 }}>
      {Array.from({ length: segments }).map((_, i) => {
        const segIndex = i + 1;
        const fill =
          segIndex < current
            ? completedColor
            : segIndex === current
              ? theme.accent
              : theme.border;
        return (
          <View
            key={i}
            style={{
              flex: 1,
              height,
              borderRadius: height / 2,
              backgroundColor: fill,
            }}
          />
        );
      })}
    </View>
  );
}
